import React from 'react';
import { ShieldX, Star, ArrowRight, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useVerificationStatus } from '../hooks/useVerificationStatus';

interface VerificationGuardProps {
  children: React.ReactNode;
  requireVerification?: boolean;
}

const VerificationGuard: React.FC<VerificationGuardProps> = ({
  children,
  requireVerification = true
}) => {
  const { isVerified, loading } = useVerificationStatus();

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If verification is not required or user is verified, show the content
  if (!requireVerification || isVerified) {
    return <>{children}</>;
  }

  // Show verification required message
  return (
    <div className="max-w-3xl mx-auto py-12 px-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
        {/* Icon */}
        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldX className="w-10 h-10 text-yellow-600" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Verification Required
        </h1>

        {/* Message */}
        <p className="text-lg text-gray-600 mb-6 leading-relaxed">
          Complete your skills verification to unlock the full potential of VetriaAI and access all features.
        </p>

        {/* Benefits */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
          <h3 className="font-semibold text-blue-900 mb-4 flex items-center">
            <Star className="w-5 h-5 mr-2" />
            Why Verify Your Skills?
          </h3>
          <ul className="text-blue-800 space-y-3">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Access all available job tests and opportunities</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Get a verified badge on your profile</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Stand out to employers with verified credentials</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Quick AI-generated assessment based on your resume</span>
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/job-seeker/resume-upload"
            className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Upload className="w-5 h-5 mr-2" />
            Upload Resume First
          </Link>

          <Link
            to="/job-seeker/skills-validation"
            className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            <Star className="w-5 h-5 mr-2" />
            Verify Skills Now
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>

        {/* Additional Note */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Need 60% or higher score to get verified • Quick 20-question assessment
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationGuard;
